/**
 * @fileoverview Atom: the small outline icon set the app draws on.
 * @module components/atoms/Icon
 */

import "./Icon.css"

/** Every icon the app can render. */
export type IconName =
	| "sound"
	| "sound-off"
	| "replay"
	| "shuffle"
	| "lock"
	| "close"
	| "check"
	| "swap"

/** Props accepted by {@link Icon}. */
export interface IconProps {
	/** Which glyph to draw. */
	readonly name: IconName
	/** Edge length in pixels. Defaults to 20. */
	readonly size?: number
}

/** Stroke path for each glyph, drawn on a 24×24 grid. */
const PATHS: Record<IconName, string> = {
	sound: "M4 9.5h3.5L12 5.5v13L7.5 14.5H4ZM15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11",
	"sound-off": "M4 9.5h3.5L12 5.5v13L7.5 14.5H4ZM16 9.5l5 5M21 9.5l-5 5",
	replay: "M20 12a8 8 0 1 1-2.5-5.8M20 4v4.5h-4.5",
	shuffle: "M4 7h3l10 10h3M17 4l3 3-3 3M4 17h3l3-3M17 20l3-3-3-3",
	lock: "M7 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1ZM9 11V8a3 3 0 0 1 6 0v3",
	close: "M6 6 18 18M18 6 6 18",
	check: "M5 13 9.5 17.5 19 7",
	swap: "M7 5v14M4 8l3-3 3 3M17 19V5M14 16l3 3 3-3",
}

/**
 * Outline icon, decorative by default.
 *
 * @param props - Which glyph to draw and its size.
 * @returns The rendered SVG. Pair it with a labelled control — it carries no
 *   accessible name of its own.
 *
 * @example
 * <Button ariaLabel="Rejouer la voix"><Icon name="replay" /></Button>
 */
export function Icon({ name, size = 20 }: IconProps) {
	return (
		<svg
			className="icon"
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d={PATHS[name]} />
		</svg>
	)
}
